import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';
import { PolicyService } from 'src/app/policy/services/policy.service';
import { EmployeeDatetimeType } from '../../../../setting/models/EmployeeDatetimeType';
import { DatetimesTypesStoreService } from '../../../../setting/services/datetimes-types-store.service';
import { CompanyEmployeePolicy, CompanyPolicyDatetimeType } from '../../../models/CompanyEmployeePolicy';

@Component({
    selector: 'app-create-edit-employee-policy-modal',
    templateUrl: './create-edit-employee-policy-modal.component.html',
    styleUrls: ['./create-edit-employee-policy-modal.component.css']
})
export class CreateEditEmployeePolicyModalComponent implements OnInit {
    public companyEmployeePolicy: CompanyEmployeePolicy;
    public policyEditForm: UntypedFormGroup;
    public selectedDatetimeTypes: EmployeeDatetimeType[] = [];
    public selectableDatetimeTypes: EmployeeDatetimeType[] = [];
    public allDatetimeTypes: EmployeeDatetimeType[] = [];
    public timeframes$: Observable<any>;
    public datetimeTypes$: Observable<EmployeeDatetimeType[]>;

    public submitted = false;

    public constructor(
        public activeModal: NgbActiveModal,
        private _fb: UntypedFormBuilder,
        private _policyService: PolicyService,
        private _datetimeTypesStore: DatetimesTypesStoreService,
    ) { }

    public get employeeDatetimeTypes(): FormArray {
        return this.policyEditForm?.get('employee_datetime_types') as FormArray;
    }

    public get f(): { [formControlName: string]: AbstractControl; } {
        return this.policyEditForm.controls;
    }

    public employeeDatetimeTypeIDControl(index: number): AbstractControl {
        return this.employeeDatetimeTypes.get(index.toString() + '.employee_datetime_type_ID');
    }

    public parentDatetimeTypeIDControl(index: number): AbstractControl {
        return this.employeeDatetimeTypes.get(index.toString() + '.parent_datetime_type_ID');
    }

    public hoursControl(index: number): AbstractControl {
        return this.employeeDatetimeTypes.get(index.toString() + '.hours');
    }

    public parentEnabled(index: number): AbstractControl {
        return this.employeeDatetimeTypes.get(index.toString() + '.parent_enabled');
    }

    public ngOnInit(): void {
        this.policyEditForm = this._fb.group({
            name: ['', Validators.required],
            legal_pause: [0, Validators.required],
            legal_pause_treshold: [0, Validators.required],
            employee_datetime_types: this._fb.array([this.datetimeFormGroup()])
        });

        if (this.companyEmployeePolicy) {
            this.policyEditForm.patchValue(this.companyEmployeePolicy);

            const datetimeTypes = this.companyEmployeePolicy.employee_datetime_types;
            if (datetimeTypes.length > 0) {
                this.employeeDatetimeTypes.clear();

                datetimeTypes.forEach((type, index) => {
                    const datetimeTypeToSave = {...type};
                    if (type.parent_datetime_type_ID) {
                        datetimeTypeToSave.parent_enabled = true;
                    }
                    this.addNewDatetimeType(datetimeTypeToSave);
                });
            }
        }

        this.datetimeTypes$ = this._datetimeTypesStore.datetimeTypes$
            .pipe(tap((datetimeTypes: EmployeeDatetimeType[]) => {
                this.allDatetimeTypes = datetimeTypes;
                this.selectableDatetimeTypes = datetimeTypes;
                setTimeout(() => {
                    this.onEmployeeDatetimeTypeChange();
                }, 500);
            }));

        this.timeframes$ = this._policyService.getTimeframes().pipe(
            shareReplay()
        );
    }

    public datetimeFormGroup(type: CompanyPolicyDatetimeType = null): UntypedFormGroup {
        return this._fb.group({
            employee_datetime_type_ID: [type?.employee_datetime_type_ID || null, Validators.required],
            parent_datetime_type_ID: [type?.parent_datetime_type_ID || null],
            hours: [type?.hours || null, Validators.required],
            range: [type?.range || 'YEAR', Validators.required],
            can_overflow: [type?.can_overflow || false, Validators.required],
            parent_enabled: [type?.parent_enabled || false],
        });
    }

    public deletePolicy(): void {
        this._policyService.deletePolicy(this.companyEmployeePolicy.company_employee_policy_ID)
            .then(
                () => this.activeModal.close('delete'),
                () => { }
            );
    }

    public onSubmit(): void {
        this.submitted = true;

        if (!this.policyEditForm.valid) {
            return;
        }

        let promise = null;

        if (this.companyEmployeePolicy) {
            promise = this._policyService.savePolicy(this.policyEditForm.value, this.companyEmployeePolicy.company_employee_policy_ID);
        } else {
            promise = this._policyService.savePolicy(this.policyEditForm.value);
        }

        promise
            .then(() => this.activeModal.close());
    }

    public addNewDatetimeType(type: CompanyPolicyDatetimeType = null): void {
        this.employeeDatetimeTypes.push(this.datetimeFormGroup(type));
    }

    public removeDatetimeType(index: number): void {
        this.employeeDatetimeTypes.removeAt(index);
        this.onEmployeeDatetimeTypeChange();
    }

    public isParentEnabled(index: number): boolean {
        return this.parentEnabled(index).value === true;
    }

    public isCreateButtonDisabled(): boolean {
        return this.employeeDatetimeTypeIDControl(this.employeeDatetimeTypes.length - 1)?.value === null;
    }

    public getSelectedDatetimeType(datetimeTypes: EmployeeDatetimeType[], index: number): string {
        return datetimeTypes.find((type: EmployeeDatetimeType) => {
            return type.employee_datetime_type_ID === this.employeeDatetimeTypeIDControl(index).value;
        }).name;
    }

    public onParentEnabledChange($event: Event, index: number): void {
        const checked = ($event.target as HTMLInputElement).checked;

        if (checked) {
            this.parentDatetimeTypeIDControl(index).setValidators([Validators.required]);
        } else {
            this.parentDatetimeTypeIDControl(index).setValue(null);
            this.parentDatetimeTypeIDControl(index).clearValidators();
        }

        this.parentDatetimeTypeIDControl(index).updateValueAndValidity();
    }

    public onEmployeeDatetimeTypeChange(): void {
        this.selectedDatetimeTypes = this.employeeDatetimeTypes.controls.map((control: UntypedFormGroup) => {
            return this.allDatetimeTypes.find(type => type.employee_datetime_type_ID === control.get('employee_datetime_type_ID').value);
        });
        this.selectableDatetimeTypes = this.allDatetimeTypes.filter((type: EmployeeDatetimeType) => {
            return !this.selectedDatetimeTypes.includes(type);
        });

        this.employeeDatetimeTypes.controls.forEach((control: UntypedFormGroup) => {
            const parentControl = control.get('parent_datetime_type_ID');
            if (!this.selectedDatetimeTypes.find(type => type?.employee_datetime_type_ID === parentControl.value)) {
                parentControl.setValue(null);
                parentControl.updateValueAndValidity();
            }
        });
    }

    public getItemsForParentSelect(index: number, selectedDatetimeTypes: EmployeeDatetimeType[]): EmployeeDatetimeType[] {
        const valueID = this.employeeDatetimeTypeIDControl(index).value;
        if (selectedDatetimeTypes.length <= 1 || selectedDatetimeTypes?.[0] === undefined) {
            return [];
        }
        return selectedDatetimeTypes.filter(type => type?.employee_datetime_type_ID !== valueID);
    }
}
